/**
 * swrCache — stale-while-revalidate localStorage cache for hot PUBLIC
 * content GETs (stories, athletes, network, collections…).
 *
 * Why: on native iOS every page fired 10-20 network round-trips to
 * flythouse.com before anything painted (5-10s on cellular). With this
 * adapter the LAST loaded payload paints in 0ms and a background fetch
 * silently refreshes the cache so the next visit is fresh.
 *
 * Safety: only whitelisted, non-personalized endpoints are cached.
 * /api/me, /api/auth, dashboards, share-page and live/breaking feeds
 * always hit the network.
 */
import axios from 'axios';

const KEY_PREFIX = 'flyt:swr:';
const FRESH_MS = 60_000;              // younger than this → no revalidate
// iter358 · Native perf — extend the max-age from 24h to 7d on native
// iOS (Capacitor) so re-opens after long pauses still paint from the
// on-device cache. Web keeps the shorter window to respect editorial
// updates within the same day.
const IS_NATIVE = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
const MAX_AGE_MS = IS_NATIVE ? 7 * 24 * 3600_000 : 24 * 3600_000;
const MAX_ENTRY_BYTES = 900_000;
const MAX_ENTRIES = IS_NATIVE ? 120 : 60;

const CACHEABLE_PREFIXES = [
  '/api/stories', '/api/stories-layout', '/api/collections',
  '/api/athletes', '/api/network/', '/api/brand-networks',
  '/api/school-network/', '/api/seo/meta', '/api/team',
  '/api/featured-reel', '/api/builders', '/api/visionaries',
];
// Never cache: viewer-personalized or live/breaking surfaces.
const NEVER_SUBSTRINGS = ['/share-page', 'breaking=true', 'nocache'];

const inflight = new Set();
// iter358 · Native perf — in-memory hot layer. Once a payload is
// read/written we keep a ref-object in this Map so repeated reads
// within the same session skip the JSON.parse round-trip entirely.
// Cleared when the tab is discarded (which is what we want).
const memCache = new Map();

const pathAndQuery = (config) => {
  try {
    const raw = axios.getUri(config);
    const u = new URL(raw, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return null;
  }
};

const isCacheable = (config, pq) => {
  if (!pq) return false;
  if ((config.method || 'get').toLowerCase() !== 'get') return false;
  if (NEVER_SUBSTRINGS.some((s) => pq.includes(s))) return false;
  return CACHEABLE_PREFIXES.some((p) => pq.startsWith(p));
};

const readEntry = (key) => {
  const mem = memCache.get(key);
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.d === undefined) return null;
    memCache.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
};

const writeEntry = (key, data) => {
  try {
    const entry = { t: Date.now(), d: data };
    memCache.set(key, entry);
    const payload = JSON.stringify(entry);
    if (payload.length > MAX_ENTRY_BYTES) return;
    localStorage.setItem(KEY_PREFIX + key, payload);
    pruneIfNeeded();
  } catch {
    /* quota / private mode — cache is a bonus, never required */
  }
};

const pruneIfNeeded = () => {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
    if (keys.length <= MAX_ENTRIES) return;
    const dated = keys.map((k) => {
      try { return [k, JSON.parse(localStorage.getItem(k))?.t || 0]; } catch { return [k, 0]; }
    }).sort((a, b) => a[1] - b[1]);
    dated.slice(0, keys.length - MAX_ENTRIES).forEach(([k]) => localStorage.removeItem(k));
  } catch { /* ignore */ }
};

const parseBody = (d) => {
  if (typeof d !== 'string') return d;
  try { return JSON.parse(d); } catch { return undefined; }
};

export function installSwrCache() {
  const baseAdapter = axios.getAdapter(axios.defaults.adapter || 'xhr');

  const revalidate = (config, key) => {
    if (inflight.has(key)) return;
    inflight.add(key);
    baseAdapter({ ...config })
      .then((res) => {
        if (res.status === 200) {
          const body = parseBody(res.data);
          if (body !== undefined) writeEntry(key, body);
        }
      })
      .catch(() => { /* offline / flaky — keep the stale copy */ })
      .finally(() => inflight.delete(key));
  };

  /**
   * iter459 · Cross-family invalidation rules. Some mutations touch
   * a resource but hit an endpoint on a totally different path prefix
   * — e.g. `PUT /api/me/athlete/profile` updates the CALLING athlete
   * but the read path is `/api/network/athletes/{slug}/profile`. The
   * heuristic in `invalidateForWrite` below matches shared prefixes
   * and can't cover this. Symptom: athlete saves a new hero photo,
   * revisits their network profile, the localStorage-hydrated first
   * paint shows the OLD photo (background revalidate updates cache
   * but doesn't update the currently-painted React state). Explicit
   * rules below force a purge so the next mount re-fetches.
   *
   * Each rule: if the write path matches `match` (equal OR startsWith
   * for family prefixes), drop every cached entry whose key starts
   * with any listed prefix.
   */
  const CROSS_FAMILY_RULES = [
    {
      match: '/api/me/athlete/profile',
      prefixes: ['/api/network/athletes', '/api/athletes', '/api/network/', '/api/me', '/api/featured-reel'],
    },
    {
      match: '/api/me/upload-avatar',
      prefixes: ['/api/network/athletes', '/api/athletes', '/api/network/', '/api/me', '/api/featured-reel'],
    },
    // Reset claim / backfill orphans mutate athlete + user linkage
    // globally — drop every network read so the founder sees the
    // new state instantly on the next tab switch.
    {
      match: '/api/admin/athletes/',
      prefixes: ['/api/network/athletes', '/api/athletes', '/api/network/', '/api/me'],
    },
    {
      match: '/api/admin/verifications',
      prefixes: ['/api/network/athletes', '/api/athletes', '/api/network/', '/api/me'],
    },
  ];

  /**
   * iter364 · Write-through invalidation. Any non-GET request
   * (POST / PUT / PATCH / DELETE) to an endpoint like
   *   /api/admin/stories-layout
   *   /api/me/athlete/profile
   *   /api/stories/{slug}
   * is presumed to mutate state that a matching GET reads back.
   * We drop every cache entry whose key SHARES the same "family"
   * so the next GET fetches fresh. Family rules:
   *   1. The written path itself (`/api/admin/stories-layout`)
   *   2. The same path with `/admin/` stripped
   *      (`/api/stories-layout`) — admin PUT often mutates a
   *      public GET.
   *   3. Anything that starts with the written path's directory
   *      — captures list-level reads that a create/delete
   *      should invalidate (e.g. writing `/api/stories/foo` also
   *      invalidates `/api/stories`).
   *   4. iter459 · Explicit cross-family rules (see above) —
   *      catches known write→read family mismatches.
   * Cheap heuristic that eliminates the class of "I saved it but
   * it didn't show" bugs without needing per-endpoint mapping.
   */
  const invalidateForWrite = (pq) => {
    if (!pq) return;
    const bare = pq.split('?')[0];
    const drops = new Set();
    drops.add(pq);
    drops.add(bare);
    if (bare.includes('/admin/')) {
      drops.add(bare.replace('/admin/', '/'));
    }
    // list-level invalidations (parent path)
    const parent = bare.replace(/\/[^/]+$/, '');
    if (parent && parent !== bare) drops.add(parent);

    // iter459 · Union in cross-family prefixes.
    const extraPrefixes = new Set();
    for (const rule of CROSS_FAMILY_RULES) {
      if (bare === rule.match || bare.startsWith(rule.match)) {
        for (const p of rule.prefixes) extraPrefixes.add(p);
      }
    }

    // Walk the memCache + localStorage and drop anything matching
    // either an exact hit or a startsWith on a drop candidate.
    const dropOne = (key) => {
      memCache.delete(key);
      try { localStorage.removeItem(KEY_PREFIX + key); } catch { /* ignore */ }
    };
    for (const d of drops) dropOne(d);
    // startsWith sweep — catches query-string variants too
    try {
      const prefixes = [...drops];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(KEY_PREFIX)) continue;
        const raw = k.slice(KEY_PREFIX.length);
        if (prefixes.some((p) => raw.startsWith(p + '?') || raw === p)) {
          dropOne(raw);
          continue;
        }
        // iter459 · Cross-family sweep. Any entry whose path starts
        // with an extra prefix (e.g. `/api/network/athletes`) is
        // wiped — even if the write path was `/api/me/athlete/profile`.
        for (const p of extraPrefixes) {
          if (raw === p || raw.startsWith(p + '/') || raw.startsWith(p + '?')) {
            dropOne(raw);
            break;
          }
        }
      }
      // Also purge from memCache (Map iteration is separate)
      for (const key of Array.from(memCache.keys())) {
        if (prefixes.some((p) => key.startsWith(p + '?') || key === p)) {
          memCache.delete(key);
          continue;
        }
        for (const p of extraPrefixes) {
          if (key === p || key.startsWith(p + '/') || key.startsWith(p + '?')) {
            memCache.delete(key);
            break;
          }
        }
      }
    } catch { /* private mode / quota — cache is a bonus */ }
  };

  axios.defaults.adapter = async (config) => {
    const pq = pathAndQuery(config);
    if (!isCacheable(config, pq)) {
      // iter364 · Every write invalidates the family so the next
      // read is guaranteed to be fresh. Fixes admin "Save layout"
      // showing stale data after save.
      const method = (config.method || 'get').toLowerCase();
      if (method !== 'get' && method !== 'head') {
        invalidateForWrite(pq);
      }
      return baseAdapter(config);
    }

    const hit = readEntry(pq);
    const now = Date.now();
    if (hit && now - hit.t < MAX_AGE_MS) {
      if (now - hit.t > FRESH_MS) revalidate(config, pq);
      return {
        data: hit.d,
        status: 200,
        statusText: 'OK',
        headers: { 'x-flyt-swr': 'hit' },
        config,
        request: null,
      };
    }

    const res = await baseAdapter(config);
    if (res.status === 200) {
      const body = parseBody(res.data);
      if (body !== undefined) writeEntry(pq, body);
    }
    return res;
  };
}
